/**
 * Interpreta lo que el jefe comercial pega desde el Excel. Las celdas llegan
 * separadas por tabulaciones, una fila por línea. Las columnas del Excel
 * original son "Nombre y Apellido", "DNI", "Plan" y una cuarta sin título que
 * repite el DNI, pero no se confía en el orden: el DNI es la celda que parece
 * un DNI, el plan la que nombra un plan y el nombre la primera que no es
 * ninguna de las dos.
 */

import { esDniValido, limpiarDni } from './altasUi';

export interface FilaPegada {
  fila: number;
  dni: string | null;
  nombre: string | null;
  /** Plan reconocido, o null si la celda no se entendió. */
  plan: string | null;
  /** Texto original de la celda de plan, para mostrar qué no se entendió. */
  planTexto: string | null;
  /** Por qué no se puede importar tal cual. null = lista para importar. */
  error: string | null;
}

export function normalizarDni(celda: string): string | null {
  const digits = limpiarDni(celda);
  return esDniValido(digits) ? digits : null;
}

/**
 * "Plan 7000", "Plan7000", "active 7000" → "Active 7000". Machea por el
 * número del plan, que es lo que se escribe igual en todos lados; si no hay
 * número ("Plan Joven"), por el nombre.
 */
export function reconocerPlan(celda: string, planes: string[]): string | null {
  const numero = celda.match(/\d{4}/)?.[0];
  if (numero) return planes.find((p) => p.includes(numero)) ?? null;
  // Sacando "plan" tiene que quedar una sola palabra: "Plan Joven" o "joven" sí;
  // "Joven, Persona" es un nombre, no un plan.
  const palabras = celda
    .toLowerCase()
    .split(/[^a-záéíóú]+/)
    .filter((w) => w && w !== 'plan');
  if (palabras.length !== 1) return null;
  return planes.find((p) => p.toLowerCase().includes(palabras[0])) ?? null;
}

/** Títulos de columna del Excel. Exactos, para no confundir "Plan 7000" con "Plan". */
function esEncabezado(celdas: string[]): boolean {
  return celdas.some((c) => /^(dni|nombre( y apellido)?|plan)$/i.test(c.trim()));
}

export function parsearPegado(texto: string, planes: string[]): FilaPegada[] {
  const lineas = texto.split(/\r?\n/).filter((l) => l.trim() !== '');
  const filas: FilaPegada[] = [];

  lineas.forEach((linea, i) => {
    const celdas = linea.split('\t').map((c) => c.trim());
    if (i === 0 && esEncabezado(celdas)) return;

    let dni: string | null = null;
    let plan: string | null = null;
    let planTexto: string | null = null;
    let nombre: string | null = null;

    celdas.forEach((celda) => {
      if (celda === '') return;
      if (dni === null && normalizarDni(celda)) {
        dni = normalizarDni(celda);
        return;
      }
      if (plan === null && planTexto === null && !normalizarDni(celda)) {
        const reconocido = reconocerPlan(celda, planes);
        if (reconocido || /\d{4}/.test(celda)) {
          planTexto = celda;
          plan = reconocido;
          return;
        }
      }
      if (nombre === null && !/^\d[\d.\s]*$/.test(celda)) {
        nombre = celda;
      }
    });

    let error: string | null = null;
    if (dni === null) error = 'No se encontró un DNI de 6 a 9 dígitos';
    else if (plan === null)
      error = planTexto ? `Plan no reconocido: "${planTexto}"` : 'Falta el plan';

    filas.push({ fila: i + 1, dni, nombre, plan, planTexto, error });
  });

  return filas;
}
